/**
 * Utility functions for validating sale lines for preauthorization.
 * Preauthorization is only allowed for non-inventory items and miscellaneous sale lines.
 */

/**
 * Checks if a sale line represents an inventory item (not allowed for preauth).
 *
 * @param {Object} saleLine - The sale line object from the sale
 * @returns {boolean} - True if the line is an inventory item (not allowed for preauth)
 */
export function isInventoryItem(saleLine) {
  // Skip item fee lines - they don't count as inventory items
  if (saleLine.lineType === 'item_fee' || saleLine.lineType === 'item_fee_refund') {
    return false;
  }

  // Miscellaneous items (itemID = 0) are allowed for preauth
  const itemID = parseInt(saleLine.itemID, 10) || 0;
  if (itemID <= 0) {
    return false;
  }

  // Check if Item relation is present and has itemType
  const item = saleLine.Item;
  if (!item) {
    // If no Item relation, assume it's allowed (edge case)
    return false;
  }

  // Non-inventory items are allowed for preauth
  if (item.itemType === 'non_inventory') {
    return false;
  }

  // This is an inventory item - not allowed for preauth
  return true;
}

/**
 * Returns an array of sale line IDs that contain inventory items.
 *
 * @param {Object} saleManager - The SaleManager instance
 * @returns {number[]} - Array of saleLineIDs that are inventory items
 */
export function getInventorySaleLineIds(saleManager) {
  const saleLines = saleManager?.sale?.get('SaleLines')?.SaleLine || [];

  return saleLines.filter(isInventoryItem).map((line) => parseInt(line.saleLineID, 10));
}

/**
 * Checks if the sale contains any inventory items.
 *
 * @param {Object} saleManager - The SaleManager instance
 * @returns {boolean} - True if the sale contains inventory items
 */
export function hasInventoryItems(saleManager) {
  return getInventorySaleLineIds(saleManager).length > 0;
}
